import { AVATARS } from "@shared/palette";
import type { GameId, Lang, RoomView } from "@shared/types";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Btn } from "../components/Button";
import { Header } from "../components/Header";
import { Eye } from "../components/Icon";
import { IdentityForm, type Identity } from "../components/IdentityForm";
import { toast } from "../components/Toast";
import { useI18n } from "../i18n";
import { api, errorCode } from "../lib/api";
import { IS_DEMO } from "../lib/asset";
import { useAuth } from "../lib/auth";
import { me, saveSeat, seats } from "../lib/prefs";
import { useStore } from "../lib/store";

const CODE_RE = /[^ABCDEFGHJKMNPQRSTUVWXYZ]/g;

interface RoomInfo {
  code: string;
  game: GameId;
  lang: Lang;
  phase: "lobby" | "game";
  players: number;
  max: number;
  /** avatars already taken in the room (older servers don't send it) */
  avatars?: number[];
}

export function JoinPage({ code: initial }: { code?: string }) {
  const { d, f } = useI18n();
  const [, nav] = useLocation();
  const { user } = useAuth();
  const saved = useStore(me);
  const allSeats = useStore(seats);
  const [code, setCode] = useState((initial ?? "").toUpperCase().replace(CODE_RE, ""));
  const [info, setInfo] = useState<RoomInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState<Identity>({ name: saved.name || user?.displayName || "", avatar: saved.avatar ?? user?.avatar ?? Math.floor(Math.random() * 19) });

  // existing seat → straight back into the room
  useEffect(() => {
    if (initial && allSeats[initial.toUpperCase()]) nav(`/room/${initial.toUpperCase()}`, { replace: true });
  }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setInfo(null);
    setErr(null);
    if (code.length < 4 || IS_DEMO) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api<{ ok: true; room: RoomInfo }>(`/rooms/${code}`, { signal: ctrl.signal })
        .then((r) => setInfo(r.room))
        .catch((e) => setErr(errorCode(e)));
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [code]);

  // an avatar belongs to one player per room: if ours is taken there, pick a free one
  useEffect(() => {
    const taken = info?.avatars;
    if (!taken?.includes(id.avatar)) return;
    const free = AVATARS.map((_, i) => i).filter((i) => !taken.includes(i));
    if (free.length) setId((v) => ({ ...v, avatar: free[Math.floor(Math.random() * free.length)] }));
  }, [info]); // eslint-disable-line react-hooks/exhaustive-deps

  async function join(role: "player" | "audience") {
    if (!id.name.trim()) return toast(d.create.nameRequired, "bad");
    setBusy(true);
    try {
      const r = await api<{ ok: true; code: string; playerId: string; token: string; view: RoomView }>(`/rooms/${code}/join`, {
        method: "POST",
        body: { name: id.name.trim(), avatar: id.avatar, role },
      });
      me.set({ name: id.name.trim(), avatar: id.avatar });
      saveSeat(r.code, { playerId: r.playerId, token: r.token, name: id.name.trim() });
      nav(`/room/${r.code}`);
    } catch (e) {
      const c = errorCode(e);
      toast((d.err as Record<string, string>)[c] ?? d.err.server, "bad");
    } finally {
      setBusy(false);
    }
  }

  const full = info ? info.players >= info.max : false;
  return (
    <div className="min-h-dvh">
      <Header />
      <main className="shell max-w-xl py-10">
        <h1 className="display text-4xl sm:text-5xl">{d.join.title}</h1>
        {IS_DEMO && <p className="mt-4 text-ink-2">{d.demo.serverOnly}</p>}
        <div className="mt-8 space-y-6">
          <div>
            <label className="label" htmlFor="join-code">{d.join.code}</label>
            <input
              id="join-code"
              className="field text-center text-3xl font-black tracking-[0.4em] uppercase"
              value={code}
              placeholder={d.join.codePh}
              maxLength={5}
              autoCapitalize="characters"
              autoComplete="off"
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(CODE_RE, ""))}
            />
            {err === "not_found" && <p className="mt-2 font-semibold text-bad">{d.join.notFound}</p>}
            {info && (
              <p className="mt-2 font-bold text-ink-2">
                {f(d.join.gameIs, { game: d.games[info.game].name })} · {d.common.lang[info.lang]} · {f(d.common.playersN, { n: info.players })}
                {info.phase === "game" && <span className="block text-ink-3">{d.join.started}</span>}
                {full && <span className="block text-flame">{d.join.full}</span>}
              </p>
            )}
          </div>
          <IdentityForm value={id} onChange={setId} taken={info?.avatars ?? []} />
          <div className="flex flex-col gap-3 sm:flex-row">
            <Btn size="lg" block disabled={!info || busy || IS_DEMO} onClick={() => join("player")}>
              {d.join.go}
            </Btn>
            <Btn variant="soft" size="lg" block disabled={!info || busy || IS_DEMO} icon={<Eye size={20} />} onClick={() => join("audience")}>
              {d.join.asAudience}
            </Btn>
          </div>
        </div>
      </main>
    </div>
  );
}
